import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";
import { CartProduct } from "../../assets/icons/icons";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";

const API_BASE = "https://laravel-api.aj-mart.com";

export default function Accessories() {
  const { t, i18n } = useTranslation();
  UseScrollAnimation();
  const [filter, setFilter] = useState("all");
  const [dataFilter, setDataFilter] = useState([
    { accessories_category_name_E: "All", accessories_category_name_A: "الكل", accessories_category_id: "all" },
  ]);
  const [dataAccessories, setDataAccessories] = useState([]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 767);
  const [visibleCount, setVisibleCount] = useState(() => typeof window !== "undefined" && window.innerWidth <= 767 ? 4 : 7);
  const isArabic = i18n.language?.startsWith("ar");

  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE}/api/accessoriesCategory`)
      .then((response) => {
        if (!active) return;
        const categories = response.data?.accessories_category;
        setDataFilter([
          { accessories_category_name_E: "All", accessories_category_name_A: "الكل", accessories_category_id: "all" },
          ...(Array.isArray(categories) ? categories : []),
        ]);
      })
      .catch((error) => console.error("Error fetching categories:", error));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE}/api/accessories`)
      .then((response) => {
        if (!active) return;
        const accessories = response.data?.accessories;
        setDataAccessories(Array.isArray(accessories) ? accessories : Array.isArray(accessories?.data) ? accessories.data : []);
      })
      .catch((error) => console.error("Error fetching accessories:", error));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => { setVisibleCount(isMobile ? 4 : 7); }, [isMobile]);

  const changeFilter = (categoryId) => {
    setFilter(String(categoryId));
    setVisibleCount(isMobile ? 4 : 7);
  };

  const filteredData = dataAccessories.filter((item) =>
    filter === "all" || String(item.accessories_category_id) === filter
  );

  const getLocalizedText = (arabic, english) =>
    (isArabic ? arabic || english : english || arabic) || "";

  const getImageUrl = (image) => {
    if (!image) return "";
    if (/^https?:\/\//i.test(image)) return image;
    return `${API_BASE}/storage/app/public/${String(image).replace(/^\/+/, "")}`;
  };

  return (
    <section className="relative py-[40px]">
      <div className="container">
        <div className="direction-text-ar js-scroll fade-top page-title mb-[20px]">
          {t("Popular")} <span>{t("Accessories")}</span>
        </div>
        <div className="flex flex-col gap-[15px]">
          <Swiper slidesPerView="auto" spaceBetween={10} navigation={false} loop={false} style={isMobile ? { direction: "ltr" } : {}}>
            {dataFilter.map((item) => {
              const categoryId = String(item.accessories_category_id);
              return (
                <SwiperSlide key={categoryId} style={{ width: "fit-content" }}>
                  <button type="button" className={`filter ${filter === categoryId ? "active" : ""}`}
                    onClick={() => changeFilter(categoryId)}
                    style={{ padding: "10px 20px", whiteSpace: "nowrap", border: "1px solid #ddd", borderRadius: "5px" }}>
                    {getLocalizedText(item.accessories_category_name_A, item.accessories_category_name_E)}
                  </button>
                </SwiperSlide>
              );
            })}
          </Swiper>

          <div className="flex flex-wrap gap-[10px] justify-start">
            {filteredData.slice(0, visibleCount).map((el) => {
              const price = Number(el.accessories_price) || 0;
              const discount = Math.min(100, Math.max(0, Number(el.accessories_price_discount) || 0));
              const priceAfterDiscount = (price * (1 - discount / 100)).toFixed(2);
              const hasVariants = Array.isArray(el.variants) && el.variants.length > 0;
              const inStock = hasVariants
                ? el.variants.some((variant) => Number(variant.quantity) > 0)
                : Number(el.accessories_stock_available) > 0;
              const accessoriesName = getLocalizedText(el.accessories_name_A, el.accessories_name_E);
              const accessoriesDesc = getLocalizedText(el.accessories_desc_A, el.accessories_desc_E);
              const detailsUrl = `/accessoriesdetails/${el.accessories_id}`;
              return (
                <div key={el.accessories_id} className="flex flex-col h-auto bg-[#f6f6f6] w-[250px] overflow-hidden border border-[#d1d1d1] rounded-[4px] justify-between">
                  <Link to={detailsUrl} className="flex flex-col flex-1">
                    <div className="w-[250px] h-[250px] relative border-b border-[#1485a6]">
                      <img className="w-full h-full object-contain" src={getImageUrl(el.accessories_main_img)} alt={accessoriesName} loading="lazy" />
                      {discount > 0 && (
                        <div className="w-10 h-10 bg-[#1485a6] rounded-full text-white font-bold flex items-center justify-center absolute top-[10px] right-[10px]">
                          {discount}%
                        </div>
                      )}
                    </div>
                    <div className="p-[10px]">
                      <div className="text-[18px] font-bold text-center capitalize">{accessoriesName}</div>
                      <p className="direction-ar-rt text-[13px] text-[#666666] text-center">
                        {accessoriesDesc.length > 75 ? `${accessoriesDesc.substring(0, 75)}...` : accessoriesDesc}
                      </p>
                      <div className="flex items-center justify-center gap-[5px] text-center">
                        <span className={`font-bold ${inStock ? "text-[#18AB00]" : "text-red-500"}`}>{priceAfterDiscount} {t("JOD")}</span>
                        {discount > 0 && <span className="line-through">{price.toFixed(2)} {t("JOD")}</span>}
                      </div>
                      <div className={`text-center font-bold ${inStock ? "text-[#18AB00]" : "text-red-500"}`}>
                        {t(inStock ? "In Stock" : "Out of Stock")}
                      </div>
                    </div>
                  </Link>
                  <Link to={detailsUrl} className="bg-[#1485a6] flex p-1.5 items-center justify-center gap-1.5 text-white font-bold rounded-tl-[20px] rounded-tr-[20px] transition-opacity duration-300">
                    <CartProduct /> {t("Add To Cart")}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
        <div className="text-center mt-5 flex items-center justify-center">
          <Link to={`/productgridtwo?category=${encodeURIComponent(filter)}`} className="flex items-center justify-center my-[15px]">
            <div className="btn js-scroll fade-top">{t("Show More")}</div>
          </Link>
        </div>
      </div>
    </section>
  );
}
